import { CliError } from "../cli/cli-error";
import { GLOBAL_BOOLEANS } from "@/lib/args/args-constants";
import type { ArgsSpec } from "@/lib/args/args-spec";
import type { ParsedArgs } from "@/lib/parsed/parsed-args";
import { parseFlag } from "@/lib/args/parse-flag";

/**
 * A deliberately small parser: no dependency, and the grammar stays something an
 * agent can generate blind: `hepha <group> <command> [args] [--flags]`. Strict
 * on purpose: `hepha keys create x --scpoe admin` used to mint a key with the
 * DEFAULT scopes and exit 0.
 */
export class ArgsParser {
  parse(argv: string[]): ParsedArgs {
    const parsed: ParsedArgs = {
      positional: [],
      flags: {},
      repeated: {},
      booleans: new Set(),
    };

    let flagsEnded = false;

    for (let index = 0; index < argv.length; index += 1) {
      const token = argv[index];
      if (token === undefined) {
        break;
      }

      // `--` ends option parsing: a prompt or a steering message is allowed to
      // start with dashes, and without this there is no way to send one.
      if (!flagsEnded && token === "--") {
        flagsEnded = true;
        continue;
      }

      if (flagsEnded || !token.startsWith("--")) {
        parsed.positional.push(token);
        continue;
      }

      index = parseFlag(argv, index, parsed);
    }

    return parsed;
  }

  /**
   * Refuse anything this action does not understand. Called once the action is
   * known, because `sessions list` and `sessions logs` accept different flags.
   */
  expect(parsed: ParsedArgs, spec: ArgsSpec): void {
    const allowed = new Set<string>([
      ...GLOBAL_BOOLEANS,
      ...(spec.flags ?? []),
      ...(spec.booleans ?? []),
    ]);

    for (const name of [...Object.keys(parsed.flags), ...parsed.booleans]) {
      if (!allowed.has(name)) {
        throw new CliError(`Unknown flag --${name}.`, 2);
      }
    }

    const max = spec.positional ?? 0;
    if (parsed.positional.length > max) {
      const surplus = parsed.positional
        .slice(max)
        .map((value) => `"${value}"`)
        .join(", ");
      throw new CliError(
        `Unexpected argument${parsed.positional.length - max > 1 ? "s" : ""} ${surplus}.`,
        2,
      );
    }
  }

  /** The action name, validated against what the group actually implements. */
  action(
    parsed: ParsedArgs,
    group: string,
    allowed: readonly string[],
    fallback: string,
  ): string {
    const action = parsed.positional[0] ?? fallback;
    // Checked BEFORE the action's own arguments: `hepha approvals frobnicate`
    // used to ask for a <guardrailRequestId> it was never going to use.
    if (!allowed.includes(action)) {
      throw new CliError(
        `Unknown ${group} command "${action}". Expected one of: ${allowed.join(", ")}.`,
        2,
      );
    }
    return action;
  }

  required(parsed: ParsedArgs, index: number, name: string): string {
    const value = parsed.positional[index];
    if (!value) {
      throw new CliError(`Missing <${name}>.`, 2);
    }
    return value;
  }

  /**
   * A whole number inside a range, refused locally: `--page -1` used to come
   * back from the API as a bare "Input validation failed".
   */
  integer(
    parsed: ParsedArgs,
    name: string,
    bounds: { min?: number; max?: number } = {},
  ): number | undefined {
    const raw = parsed.flags[name];
    if (raw === undefined) {
      return undefined;
    }
    const trimmed = raw.trim();
    const value = Number(trimmed);
    // Number("") is 0, so an empty `--limit=` would quietly mean zero.
    if (trimmed === "" || !Number.isFinite(value)) {
      throw new CliError(`--${name} expects a number, got "${raw}".`, 2);
    }
    if (!Number.isInteger(value)) {
      throw new CliError(`--${name} expects a whole number, got "${raw}".`, 2);
    }
    if (bounds.min !== undefined && value < bounds.min) {
      throw new CliError(`--${name} must be at least ${bounds.min}.`, 2);
    }
    if (bounds.max !== undefined && value > bounds.max) {
      throw new CliError(`--${name} must be at most ${bounds.max}.`, 2);
    }
    return value;
  }

  /** One of a fixed set, named in the error so the fix is obvious. */
  choice<T extends string>(
    parsed: ParsedArgs,
    name: string,
    allowed: readonly T[],
  ): T | undefined {
    const raw = parsed.flags[name];
    if (raw === undefined) {
      return undefined;
    }
    if (!(allowed as readonly string[]).includes(raw)) {
      throw new CliError(
        `--${name} expects one of: ${allowed.join(", ")}. Got "${raw}".`,
        2,
      );
    }
    return raw as T;
  }
}
